import { memo, useEffect, useState, type ReactNode } from 'react';
import { DecimalInput } from '../ui/DecimalInput';
import { Palette, Check, Link2, ArrowLeft, ArrowRight, LayoutTemplate, ListChecks, ChevronDown, ChevronRight, Ban, MessageSquare, Clock, MoreHorizontal, Pencil, Plus, Trash2, Gauge, ArrowRightLeft, Archive, Maximize2, Lock, Sigma, ListPlus, Upload } from 'lucide-react';
import { pastedTasks, type ParsedImport, aggregate, formatAggregate, formatNumberOf, describeGroup, dueState, formatDue, gateProblems, isGroup, LABEL_COLORS, type CardSummary, type Column, type ColumnGate, type ConditionGroup, type CustomField, type Density, type Label, type ColumnColor, type Member, type RuleRefs } from '@kaba/shared';
import { GroupEditor, useDescribeContext } from './Automations';
import { withFormConfirm } from '../../lib/formDeps';
import { api } from '../../lib/api';
import { boardKey, useBoard, useBoardAction, useCreateCard, useMoveColumn } from '../../lib/board';
import { useQueryClient } from '@tanstack/react-query';
import { byPosition, type BoardSnapshot } from '@kaba/shared';
import { toast } from '../../lib/toast';
import { PromptDialog } from '../ui/Prompt';
import { Avatar, Button, cx, DropdownContent, DropdownItem, DropdownMenu, DropdownTrigger, IconButton, LABEL_BG, LABEL_NAMES, columnLook, columnColorName, Sheet } from '../ui/ui';
import type { ColumnRenderProps } from './BoardDnd';
import { CardTile, FieldChips, menuButtonClass } from './CardTile';
import { PasteTasksPrompt } from './PasteTasksPrompt';
import { openCardImport } from '../../lib/cardImport';

/* ---------- Spalten-Menü ---------- */

function ColumnMenu({ boardId, column, cardCount }: { boardId: string; column: Column; cardCount: number }) {
  const action = useBoardAction(boardId);
  const moveColumn = useMoveColumn(boardId);
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<'rename' | 'wip' | 'rules' | 'color' | 'sum' | null>(null);
  const numericFields = (qc.getQueryData<BoardSnapshot>(boardKey(boardId))?.fields ?? []).filter((f) => f.type === 'number' || f.type === 'currency' || f.type === 'formula');
  // Spalten ohne Ziehen verschieben (Tastatur/Screenreader/Touch)
  const order = () => [...(qc.getQueryData<BoardSnapshot>(boardKey(boardId))?.columns ?? [])].sort(byPosition).map((c) => c.id);
  const shift = (step: -1 | 1) => {
    const ids = order();
    const i = ids.indexOf(column.id);
    const next = i + step;
    if (i < 0 || next < 0 || next >= ids.length) return;
    moveColumn.mutate({ columnId: column.id, index: next });
  };
  return (
    <>
      <DropdownMenu>
        <DropdownTrigger asChild>
          <IconButton label={`Menü der Spalte ${column.title}`} onPointerDown={(e) => e.stopPropagation()}>
            <MoreHorizontal size={18} />
          </IconButton>
        </DropdownTrigger>
        <DropdownContent>
          <DropdownItem icon={<Pencil size={16} />} onSelect={() => setDialog('rename')}>
            Umbenennen
          </DropdownItem>
          <DropdownItem icon={<Gauge size={16} />} onSelect={() => setDialog('wip')}>
            WIP-Limit {column.wipLimit ? `(${column.wipLimit})` : ''}
          </DropdownItem>
          <DropdownItem icon={<ListChecks size={16} />} onSelect={() => setDialog('rules')}>
            Spaltenregeln …
          </DropdownItem>
          {(numericFields.length > 0 || column.sumLimit) && (
            <DropdownItem icon={<Sigma size={16} />} onSelect={() => setDialog('sum')}>
              Summenlimit {column.sumLimit ? '(an)' : '…'}
            </DropdownItem>
          )}
          <DropdownItem icon={<Upload size={16} />} onSelect={() => openCardImport({ boardId, columnId: column.id })}>
            Karten importieren …
          </DropdownItem>
          <DropdownItem icon={<Palette size={16} />} onSelect={() => setDialog('color')}>
            Farbe {column.color ? `(${columnColorName(column.color)})` : '…'}
          </DropdownItem>
          <DropdownItem icon={<ArrowLeft size={16} />} onSelect={() => shift(-1)}>
            Nach links
          </DropdownItem>
          <DropdownItem icon={<ArrowRight size={16} />} onSelect={() => shift(1)}>
            Nach rechts
          </DropdownItem>
          <DropdownItem
            danger
            icon={<Trash2 size={16} />}
            onSelect={() => {
              if (cardCount > 0) {
                toast.error('Die Spalte enthält noch Karten. Verschiebe oder archiviere sie zuerst.');
                return;
              }
              action.mutate(async () => {
                const res = await withFormConfirm((q) => api.del<{ archivedMoved: number; to: string } | null>(`/api/columns/${column.id}${q}`));
                if (res?.archivedMoved)
                  toast.info(`Spalte gelöscht. ${res.archivedMoved} archivierte Karte(n) liegen jetzt (weiter archiviert) in „${res.to}“.`);
              });
            }}
          >
            Spalte löschen
          </DropdownItem>
        </DropdownContent>
      </DropdownMenu>
      <PromptDialog
        open={dialog === 'rename'}
        title="Spalte umbenennen"
        label="Name"
        initial={column.title}
        onClose={() => setDialog(null)}
        onSubmit={(v) => {
          setDialog(null);
          if (v.trim() && v.trim() !== column.title) action.mutate(() => api.patch(`/api/columns/${column.id}`, { title: v.trim() }));
        }}
      />
      <ColorDialog
        open={dialog === 'color'}
        column={column}
        onClose={() => setDialog(null)}
        onPick={(color) => {
          setDialog(null);
          if (color !== (column.color ?? null)) action.mutate(() => api.patch(`/api/columns/${column.id}`, { color }));
        }}
      />
      <RulesDialog
        open={dialog === 'rules'}
        column={column}
        onClose={() => setDialog(null)}
        onSave={(patch) => {
          setDialog(null);
          action.mutate(() => api.patch(`/api/columns/${column.id}`, patch));
        }}
      />
      <SumLimitDialog
        open={dialog === 'sum'}
        column={column}
        fields={numericFields}
        onClose={() => setDialog(null)}
        onSave={(sumLimit) => {
          setDialog(null);
          action.mutate(() => api.patch(`/api/columns/${column.id}`, { sumLimit }));
        }}
      />
      <PromptDialog
        open={dialog === 'wip'}
        title="WIP-Limit"
        label="Maximale Anzahl Karten (leer = kein Limit)"
        type="number"
        initial={column.wipLimit ? String(column.wipLimit) : ''}
        hint="Wird das Limit überschritten, färbt sich der Zähler rot. Gezählt werden alle Karten der Spalte, auch ausgefilterte."
        onClose={() => setDialog(null)}
        onSubmit={(v) => {
          setDialog(null);
          const n = v.trim() ? Math.max(1, Math.floor(Number(v))) : null;
          if (v.trim() && !Number.isFinite(n)) return;
          action.mutate(() => api.patch(`/api/columns/${column.id}`, { wipLimit: n }));
        }}
      />
    </>
  );
}

/** 0.82: Summenlimit – Hinweis im Spaltenkopf, wenn die Summe eines Feldes das Maximum übersteigt */
function SumLimitDialog({ open, column, fields, onClose, onSave }: { open: boolean; column: Column; fields: CustomField[]; onClose: () => void; onSave: (v: { fieldId: string; max: number } | null) => void }) {
  const [fieldId, setFieldId] = useState(column.sumLimit?.fieldId ?? fields[0]?.id ?? '');
  const [max, setMax] = useState<number | null>(column.sumLimit?.max ?? null);
  useEffect(() => {
    if (open) {
      setFieldId(column.sumLimit?.fieldId ?? fields[0]?.id ?? '');
      setMax(column.sumLimit?.max ?? null);
    }
  }, [open, column.sumLimit, fields]);
  const cls = 'min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 text-base sm:text-sm';
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title={`Summenlimit: ${column.title}`} side="center">
      <form
        className="flex flex-col gap-3 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!fieldId || max === null) return toast.error('Bitte Feld und Höchstwert angeben.');
          onSave({ fieldId, max });
        }}
      >
        <p className="text-sm text-[var(--ink-soft)]">Übersteigt die Summe aller Karten dieser Spalte den Höchstwert, färbt sich die Anzeige im Spaltenkopf rot – etwa ein Budget je Spalte. Nichts wird blockiert.</p>
        <label className="flex flex-col gap-1 text-sm">
          Feld
          <select className={cls} value={fieldId} onChange={(e) => setFieldId(e.target.value)}>
            {fields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Höchstens
          <DecimalInput className={cls} value={max} onChange={setMax} min={0} placeholder="z. B. 5.000" aria-label="Höchstens" />
        </label>
        <div className="flex justify-end gap-2">
          {column.sumLimit && (
            <Button type="button" variant="ghost" onClick={() => onSave(null)}>
              Entfernen
            </Button>
          )}
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary">
            Speichern
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

/* ---------- Spaltenregeln (Definition of Done) ---------- */

/** Spaltenfarbe wählen (0.49): Labelfarben, ab 0.51 auch Markenfarbe des Boards und eigene Farbe */
function ColorDialog({ open, column, onClose, onPick }: { open: boolean; column: Column; onClose: () => void; onPick: (c: ColumnColor | null) => void }) {
  const current = column.color ?? null;
  const isCustom = !!current && current.startsWith('#');
  const [hex, setHex] = useState<string>(isCustom ? current! : '#3b5bdb');
  useEffect(() => {
    if (open) setHex(current?.startsWith('#') ? current : '#3b5bdb');
  }, [open, current]);
  const valid = /^#[0-9a-fA-F]{6}$/.test(hex);
  const swatch = (c: ColumnColor | null, label: string, dot: ReactNode) => (
    <button
      key={c ?? 'none'}
      type="button"
      role="radio"
      aria-checked={current === c}
      onClick={() => onPick(c)}
      className={cx(
        'flex min-h-11 items-center gap-2 rounded-lg border px-2.5 text-left text-sm',
        current === c ? 'border-accent bg-accent-soft/50 font-semibold' : 'border-[var(--line)] hover:bg-black/[0.03] dark:hover:bg-white/5',
      )}
    >
      {dot}
      <span className="flex-1">{label}</span>
      {current === c && <Check size={16} aria-hidden />}
    </button>
  );
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title={`Farbe der Spalte „${column.title}“`} side="center">
      <div className="flex flex-col gap-3 p-4">
        <p className="text-sm text-[var(--ink-soft)]">Die Farbe erscheint als Streifen und zarte Fläche hinter den Karten – alle im Board sehen sie.</p>
        <div role="radiogroup" aria-label="Farbe" className="grid grid-cols-2 gap-2">
          {swatch(null, 'Keine Farbe', <span className="size-5 shrink-0 rounded-md border border-dashed border-[var(--ink-soft)]" aria-hidden />)}
          {swatch('brand', 'Markenfarbe', <span className="size-5 shrink-0 rounded-md bg-accent" aria-hidden />)}
          {LABEL_COLORS.map((c) => swatch(c, LABEL_NAMES[c], <span className={cx('size-5 shrink-0 rounded-md', LABEL_BG[c])} aria-hidden />))}
          {isCustom && swatch(current, 'Eigene Farbe', <span className="size-5 shrink-0 rounded-md" style={{ background: current! }} aria-hidden />)}
        </div>
        <p className="text-xs text-[var(--ink-soft)]">Die Markenfarbe folgt dem Erscheinungsbild des Boards – ändert es sich, ändert sich die Spalte mit.</p>
        <form
          className="flex flex-col gap-2 rounded-lg border border-[var(--line)] p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) onPick(hex.toLowerCase() as ColumnColor);
          }}
        >
          <span className="text-sm font-semibold">Eigene Farbe</span>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="color"
              aria-label="Farbe auswählen"
              value={valid ? hex.toLowerCase() : '#3b5bdb'}
              onChange={(e) => setHex(e.target.value)}
              className="h-11 w-14 cursor-pointer rounded-lg border border-[var(--line)] bg-[var(--panel)] p-1 sm:h-9"
            />
            <input
              aria-label="HEX-Wert"
              value={hex}
              onChange={(e) => setHex(e.target.value.trim())}
              maxLength={7}
              spellCheck={false}
              className={cx('min-h-11 w-28 rounded-lg border bg-[var(--panel)] px-2 font-mono text-sm sm:min-h-9', valid ? 'border-[var(--line)]' : 'border-rose-500')}
              aria-invalid={!valid}
            />
            <Button type="submit" disabled={!valid}>
              Übernehmen
            </Button>
          </div>
          {!valid && <p className="text-xs text-rose-700 dark:text-rose-300">Bitte als HEX-Wert angeben, z. B. #3b5bdb.</p>}
        </form>
      </div>
    </Sheet>
  );
}

/** 0.79: Eine Seite der Spaltenregel – Hinweistext und optional verbindliche Bedingungen */
function GateSide({
  side,
  text,
  setText,
  gate,
  setGate,
  canGate,
  snapshot,
}: {
  side: 'entry' | 'exit';
  text: string;
  setText: (t: string) => void;
  gate: ColumnGate | null;
  setGate: (g: ColumnGate | null) => void;
  canGate: boolean;
  snapshot: BoardSnapshot | undefined;
}) {
  const ctx = useDescribeContext(snapshot ?? EMPTY_SNAPSHOT);
  const area = 'min-h-16 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] p-2.5 text-base sm:text-sm';
  const refs = snapshot ? snapshotRefs(snapshot) : null;
  const problems = gate && refs ? gateProblems(gate, refs) : [];
  return (
    <fieldset className="flex min-w-0 flex-col gap-2 rounded-xl border border-[var(--line)] p-3">
      <legend className="px-1 text-sm font-semibold">{side === 'entry' ? 'Hineinnehmen' : 'Weitergeben'}</legend>
      <label className="flex flex-col gap-1.5 text-sm font-medium">
        {side === 'entry' ? 'Bereit für diese Spalte, wenn …' : 'Fertig für die nächste Spalte, wenn …'}
        <textarea
          className={area}
          maxLength={300}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={side === 'entry' ? 'z. B. Ansprechperson und Frist stehen fest' : 'z. B. Vier-Augen-Prüfung erledigt, Checkliste abgehakt'}
        />
      </label>
      <label className={cx('flex min-h-11 items-center gap-2 text-sm sm:min-h-9', !canGate && 'opacity-60')}>
        <input
          type="checkbox"
          className="size-5 accent-[var(--color-accent)]"
          disabled={!canGate || !snapshot}
          checked={!!gate}
          onChange={(e) => setGate(e.target.checked ? { when: { op: 'all', items: [] }, ownerOverride: true, message: null } : null)}
        />
        <Lock size={15} aria-hidden /> Verbindlich: {side === 'entry' ? 'nur hineinlassen' : 'nur weitergeben'}, wenn die Bedingungen erfüllt sind
      </label>
      {!canGate && <p className="text-xs text-[var(--ink-soft)]">Verbindliche Regeln ändern nur Besitzer*innen des Boards.</p>}
      {gate && !canGate && <p className="text-sm">Gilt: {gate.message || `nur wenn ${describeGroup(gate.when, ctx)}`}</p>}
      {gate && canGate && snapshot && (
        <div className="flex flex-col gap-2 border-l-4 border-amber-300 pl-3 dark:border-amber-700">
          <GroupEditor g={gate.when} s={snapshot} total={countItems(gate.when)} onChange={(when) => setGate({ ...gate, when })} />
          {!gate.when.items.length && <p className="text-xs text-rose-600">Füge mindestens eine Bedingung hinzu.</p>}
          {problems.map((p) => (
            <p key={p} className="text-xs text-rose-600">
              {p}
            </p>
          ))}
          <label className="flex flex-col gap-1 text-sm">
            Eigener Hinweis (statt der Bedingungen, optional)
            <input
              className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 text-base sm:min-h-9 sm:text-sm"
              maxLength={200}
              value={gate.message ?? ''}
              onChange={(e) => setGate({ ...gate, message: e.target.value || null })}
              placeholder="z. B. Freigabe durch eine zweite Person nötig"
            />
          </label>
          <label className="flex min-h-11 items-center gap-2 text-sm sm:min-h-9">
            <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={gate.ownerOverride} onChange={(e) => setGate({ ...gate, ownerOverride: e.target.checked })} />
            Besitzer*innen dürfen mit Begründung übergehen (steht im Verlauf der Karte)
          </label>
          <p className="text-xs text-[var(--ink-soft)]">
            Gilt beim Ziehen, „Verschieben nach …“, Anlegen in der Spalte und für Automationen. „Die auslösende Person“ ist, wer die Karte verschiebt.
          </p>
        </div>
      )}
    </fieldset>
  );
}

const countItems = (g: ConditionGroup): number => g.items.reduce((n, x) => n + (isGroup(x) ? countItems(x) : 1), 0);

/** Verweise des Boards für die Prüfung im Browser (Gruppen prüft der Server) */
function snapshotRefs(s: BoardSnapshot): RuleRefs {
  return {
    columns: new Set(s.columns.map((c) => c.id)),
    labels: new Set(s.labels.map((l) => l.id)),
    members: new Set(s.members.map((m) => m.id)),
    fields: new Map(s.fields.map((f) => [f.id, f])),
  };
}

const EMPTY_SNAPSHOT = { board: { id: '' }, columns: [], labels: [], members: [], fields: [] } as unknown as BoardSnapshot;

function RulesDialog({
  open,
  column,
  onClose,
  onSave,
}: {
  open: boolean;
  column: Column;
  onClose: () => void;
  onSave: (patch: {
    entryRule: string | null;
    exitRule: string | null;
    entryGate?: ColumnGate | null;
    exitGate?: ColumnGate | null;
    entryAsk?: { fieldIds: string[]; required: string[] } | null;
    locked?: boolean;
  }) => void;
}) {
  const { data: snapshot } = useBoard(column.boardId);
  const [ask, setAsk] = useState<{ fieldIds: string[]; required: string[] }>(column.entryAsk ?? { fieldIds: [], required: [] });
  const [locked, setLocked] = useState(!!column.locked);
  const canGate = snapshot?.board.role === 'owner';
  const [entry, setEntry] = useState(column.entryRule ?? '');
  const [exit, setExit] = useState(column.exitRule ?? '');
  const [entryGate, setEntryGate] = useState<ColumnGate | null>(column.entryGate ?? null);
  const [exitGate, setExitGate] = useState<ColumnGate | null>(column.exitGate ?? null);
  useEffect(() => {
    if (open) {
      setEntry(column.entryRule ?? '');
      setExit(column.exitRule ?? '');
      setEntryGate(column.entryGate ?? null);
      setExitGate(column.exitGate ?? null);
      setAsk(column.entryAsk ?? { fieldIds: [], required: [] });
      setLocked(!!column.locked);
    }
  }, [open, column.entryRule, column.exitRule, column.entryGate, column.exitGate, column.entryAsk, column.locked]);
  const askable = (snapshot?.fields ?? []).filter((f) => f.type !== 'approval' && f.type !== 'sequence');
  const refs = snapshot ? snapshotRefs(snapshot) : null;
  const invalid = [entryGate, exitGate].some((g) => !!g && (!g.when.items.length || (refs ? gateProblems(g, refs).length > 0 : false)));
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title={`Spaltenregeln: ${column.title}`} side="center">
      <form
        className="flex flex-col gap-3 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (invalid) return;
          onSave({
            entryRule: entry.trim() || null,
            exitRule: exit.trim() || null,
            entryAsk: ask.fieldIds.length ? ask : null,
            ...(canGate ? { entryGate, exitGate, locked } : {}),
          });
        }}
      >
        <p className="text-sm text-[var(--ink-soft)]">
          Der Hinweistext erscheint im Spaltenkopf und beim Verschieben. Verbindlich gemacht, hält KaBa Karten auf, bis die Bedingungen erfüllt sind – etwa für das
          Vier-Augen-Prinzip.
        </p>
        <GateSide side="entry" text={entry} setText={setEntry} gate={entryGate} setGate={setEntryGate} canGate={canGate} snapshot={snapshot} />
        <GateSide side="exit" text={exit} setText={setExit} gate={exitGate} setGate={setExitGate} canGate={canGate} snapshot={snapshot} />
        {askable.length > 0 && (
          <fieldset className="flex min-w-0 flex-col gap-2 rounded-xl border border-[var(--line)] p-3">
            <legend className="px-1 text-sm font-semibold">Beim Hineinziehen fragen nach …</legend>
            <p className="text-xs text-[var(--ink-soft)]">Wer eine Karte hierher zieht, füllt diese Felder gleich im Dialog aus – etwa „Bezahlt am“. Pflichtfelder müssen ausgefüllt sein.</p>
            {askable.map((f) => {
              const on = ask.fieldIds.includes(f.id);
              const req = ask.required.includes(f.id);
              return (
                <div key={f.id} className="flex flex-wrap items-center gap-x-4">
                  <label className="flex min-h-10 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="size-5 accent-[var(--color-accent)]"
                      checked={on}
                      onChange={(e) =>
                        setAsk(
                          e.target.checked
                            ? { ...ask, fieldIds: [...ask.fieldIds, f.id].slice(0, 8) }
                            : { fieldIds: ask.fieldIds.filter((x) => x !== f.id), required: ask.required.filter((x) => x !== f.id) },
                        )
                      }
                    />
                    {f.name}
                  </label>
                  {on && (
                    <label className="flex min-h-10 items-center gap-2 text-sm text-[var(--ink-soft)]">
                      <input
                        type="checkbox"
                        className="size-5 accent-[var(--color-accent)]"
                        checked={req}
                        onChange={(e) => setAsk({ ...ask, required: e.target.checked ? [...ask.required, f.id] : ask.required.filter((x) => x !== f.id) })}
                      />
                      Pflicht
                    </label>
                  )}
                </div>
              );
            })}
          </fieldset>
        )}
        <label className={cx('flex min-h-11 items-start gap-2 rounded-xl border border-[var(--line)] p-3 text-sm', !canGate && 'opacity-60')}>
          <input type="checkbox" className="mt-0.5 size-5 accent-[var(--color-accent)]" disabled={!canGate} checked={locked} onChange={(e) => setLocked(e.target.checked)} />
          <span>
            <span className="flex items-center gap-1 font-semibold">
              <Lock size={14} aria-hidden /> Schreibschutz
            </span>
            Karten in dieser Spalte lassen sich nur noch lesen und kommentieren; ändern und herausziehen dürfen nur Besitzer*innen. Automationen ändern sie nicht mehr.
          </span>
        </label>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={invalid}>
            Speichern
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

/** Verbindliche Regeln als Sätze (nur im geöffneten Hinweis berechnet) */
function GateLines({ column }: { column: Column }) {
  const { data: snapshot } = useBoard(column.boardId);
  const ctx = useDescribeContext(snapshot ?? EMPTY_SNAPSHOT);
  const line = (g: ColumnGate) => g.message || `nur wenn ${describeGroup(g.when, ctx)}`;
  return (
    <>
      {column.entryGate && (
        <p>
          <span className="flex items-center gap-1 text-xs font-semibold text-amber-700 uppercase dark:text-amber-400">
            <Lock size={12} aria-hidden /> Verbindlich hinein
          </span>
          {line(column.entryGate)}
        </p>
      )}
      {column.exitGate && (
        <p>
          <span className="flex items-center gap-1 text-xs font-semibold text-amber-700 uppercase dark:text-amber-400">
            <Lock size={12} aria-hidden /> Verbindlich weiter
          </span>
          {line(column.exitGate)}
        </p>
      )}
    </>
  );
}

/** Kleines Symbol im Spaltenkopf; zeigt die Regeln beim Antippen */
export function RulesHint({ column }: { column: Column }) {
  const gated = !!column.entryGate || !!column.exitGate || !!column.locked;
  if (!column.entryRule && !column.exitRule && !gated) return null;
  return (
    <DropdownMenu>
      <DropdownTrigger asChild>
        <IconButton label={`Spaltenregeln von ${column.title}${gated ? ' (verbindlich)' : ''}`} onPointerDown={(e) => e.stopPropagation()}>
          {gated ? <Lock size={15} className="text-amber-600 dark:text-amber-400" /> : <ListChecks size={16} />}
        </IconButton>
      </DropdownTrigger>
      <DropdownContent>
        <div className="flex max-w-80 flex-col gap-2 p-2.5 text-sm">
          {column.entryRule && (
            <p>
              <span className="block text-xs font-semibold text-[var(--ink-soft)] uppercase">Bereit, wenn</span>
              {column.entryRule}
            </p>
          )}
          {column.exitRule && (
            <p>
              <span className="block text-xs font-semibold text-[var(--ink-soft)] uppercase">Fertig, wenn</span>
              {column.exitRule}
            </p>
          )}
          {gated && <GateLines column={column} />}
          {column.locked && (
            <p className="flex items-center gap-1 text-xs font-semibold text-amber-700 uppercase dark:text-amber-400">
              <Lock size={12} aria-hidden /> Schreibgeschützt
            </p>
          )}
        </div>
      </DropdownContent>
    </DropdownMenu>
  );
}

export function CountBadge({ column, total, shown }: { column: Column; total: number; shown: number }) {
  const over = column.wipLimit !== null && total > column.wipLimit;
  const filtered = shown !== total;
  return (
    <span
      className={cx(
        'rounded-full px-2 py-0.5 text-xs tabular-nums',
        over ? 'bg-rose-600 font-semibold text-white' : 'bg-black/5 text-[var(--ink-soft)] dark:bg-white/10',
      )}
      title={
        (filtered ? `${shown} von ${total} Karten passen zum Filter. ` : '') +
        (column.wipLimit ? `${total} von maximal ${column.wipLimit} Karten${over ? ' – Limit überschritten' : ''}` : `${total} Karten`)
      }
    >
      {filtered ? `${shown} · ${total}` : total}
      {column.wipLimit ? `/${column.wipLimit}` : ''}
    </span>
  );
}

/* ---------- Karte hinzufügen ---------- */

export function AddCard({ boardId, columnId, compact, onCreated }: { boardId: string; columnId: string; compact?: boolean; onCreated?: (card: CardSummary) => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  /** 0.88: mehrere Aufgaben eingefügt oder eingetippt → Rückfrage statt einer Karte mit Zeilenumbrüchen */
  const [multi, setMulti] = useState<{ parsed: ParsedImport; text: string } | null>(null);
  const create = useCreateCard(boardId);
  const templates = useBoard(boardId).data?.cardTemplates ?? [];
  const submit = () => {
    const t = title.trim();
    if (!t) return;
    const parsed = pastedTasks(t);
    if (parsed) return setMulti({ parsed, text: t });
    create.mutate({ columnId, title: t.replace(/\s*\n\s*/g, ' ') }, { onSuccess: (card) => onCreated?.(card) });
    setTitle('');
  };
  if (open && multi)
    return (
      <PasteTasksPrompt
        boardId={boardId}
        columnId={columnId}
        parsed={multi.parsed}
        text={multi.text}
        onCreated={onCreated}
        onDone={() => {
          setMulti(null);
          setTitle('');
        }}
        onSingle={() => {
          create.mutate({ columnId, title: multi.text.replace(/\s*\n\s*/g, ' ').slice(0, 500) }, { onSuccess: (card) => onCreated?.(card) });
          setMulti(null);
          setTitle('');
        }}
        onCancel={() => {
          setTitle(multi.text);
          setMulti(null);
        }}
      />
    );
  if (!open)
    return (
      <button
        onClick={() => setOpen(true)}
        className={cx(
          'flex min-h-11 w-full items-center gap-1.5 rounded-lg px-2.5 text-sm text-[var(--ink-soft)] hover:bg-black/5 hover:text-[var(--ink)] dark:hover:bg-white/5',
          compact && 'min-h-10',
        )}
      >
        <Plus size={16} /> Karte hinzufügen
      </button>
    );
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <textarea
        autoFocus
        rows={2}
        value={title}
        placeholder="Titel der Karte …"
        onChange={(e) => setTitle(e.target.value)}
        onPaste={(e) => {
          // Liste aus Word, Mail, Notizen oder Excel: gleich nachfragen (nur in ein leeres Feld)
          if (title.trim()) return;
          const text = e.clipboardData.getData('text/plain');
          const parsed = pastedTasks(text);
          if (!parsed) return;
          e.preventDefault();
          setMulti({ parsed, text });
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
          if (e.key === 'Escape') setOpen(false);
        }}
        onBlur={() => {
          if (!title.trim()) setOpen(false);
        }}
        aria-describedby={`addcard-hint-${columnId}`}
        className="w-full resize-none rounded-xl border border-[var(--line)] bg-[var(--panel)] p-2.5 text-base shadow-sm outline-none focus:border-accent sm:text-sm"
      />
      <div className="flex gap-2">
        <button type="submit" className="min-h-10 rounded-lg bg-accent px-3 text-sm font-medium text-on-accent">
          Hinzufügen
        </button>
        {templates.length > 0 && (
          <DropdownMenu>
            <DropdownTrigger asChild>
              <button type="button" className="inline-flex min-h-10 items-center gap-1 rounded-lg px-2.5 text-sm hover:bg-black/5 dark:hover:bg-white/5" onMouseDown={(e) => e.preventDefault()}>
                <LayoutTemplate size={15} /> Vorlage
              </button>
            </DropdownTrigger>
            <DropdownContent align="start">
              {templates.map((t) => (
                <DropdownItem
                  key={t.id}
                  onSelect={() => {
                    // Eingetippter Titel gewinnt, sonst der Titel der Vorlage
                    create.mutate({ columnId, title: title.trim() || t.data.title, templateId: t.id }, { onSuccess: (card) => onCreated?.(card) });
                    setTitle('');
                  }}
                >
                  {t.name}
                </DropdownItem>
              ))}
            </DropdownContent>
          </DropdownMenu>
        )}
        <button
          type="button"
          title="Mehrere Karten aus einer Liste, Excel oder CSV"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            openCardImport({ boardId, columnId, text: title.trim() || undefined });
            setTitle('');
            setOpen(false);
          }}
          className="inline-flex min-h-10 items-center gap-1 rounded-lg px-2.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
        >
          <ListPlus size={15} /> Liste
        </button>
        <button type="button" onClick={() => setOpen(false)} className="min-h-10 rounded-lg px-3 text-sm hover:bg-black/5">
          Fertig
        </button>
      </div>
      <p id={`addcard-hint-${columnId}`} className="sr-only">
        Enter legt die Karte an. Mehrere Zeilen einfügen legt auf Wunsch mehrere Karten an.
      </p>
    </form>
  );
}

/* ---------- 0.82: Auswertung im Spaltenkopf ---------- */

/**
 * Summen/Durchschnitte der ausgewerteten Felder über die angezeigten Karten (Filter zählen mit). Erscheint
 * nur, wenn Felder ausgewertet werden UND Karten Werte haben; Summenlimit über alle Karten der Spalte.
 */
export function ColumnAggregates({ boardId, column, cards, filtered, className }: { boardId: string; column: Column; cards: CardSummary[]; filtered: boolean; className?: string }) {
  const { data: snap } = useBoard(boardId);
  const fields = snap?.fields.filter((f) => !!f.config?.aggregate) ?? [];
  const limitField = column.sumLimit ? snap?.fields.find((f) => f.id === column.sumLimit!.fieldId) : undefined;
  const lines = fields
    .map((f) => ({ f, text: formatAggregate(f, aggregate(f, cards.map((c) => c.custom?.[f.id]))) }))
    .filter((x): x is { f: CustomField; text: string } => !!x.text);
  let limit: { text: string; over: boolean } | null = null;
  if (limitField && column.sumLimit && snap) {
    const all = snap.cards.filter((c) => c.columnId === column.id);
    const sum = aggregate(limitField, all.map((c) => c.custom?.[limitField.id]), 'sum')?.value ?? 0;
    limit = { text: `${formatNumberOf(limitField, sum)} / ${formatNumberOf(limitField, column.sumLimit.max)}`, over: sum > column.sumLimit.max };
  }
  if (!lines.length && !limit) return null;
  return (
    <div className={cx('flex flex-wrap gap-1', className)} aria-label={`Auswertung ${column.title}${filtered ? ' (gefiltert)' : ''}`}>
      {lines.map(({ f, text }) => (
        <span key={f.id} title={`${f.name}${filtered ? ' – nur Karten, die zum Filter passen' : ''}`} className="max-w-full truncate rounded-md bg-black/5 px-1.5 py-px text-[11px] tabular-nums text-[var(--ink-soft)] dark:bg-white/10">
          <span className="opacity-70">{f.name}:</span> {text}
        </span>
      ))}
      {limit && (
        <span
          title={`Summenlimit „${limitField!.name}“ (alle Karten der Spalte)${limit.over ? ' – überschritten' : ''}`}
          className={cx('rounded-md px-1.5 py-px text-[11px] tabular-nums', limit.over ? 'bg-rose-600 font-semibold text-white' : 'bg-black/5 text-[var(--ink-soft)] dark:bg-white/10')}
        >
          {limitField!.name}: {limit.text}
        </span>
      )}
    </div>
  );
}

/* ---------- Board-Spalte ---------- */

export function BoardColumn({ p, boardId, canEdit }: { p: ColumnRenderProps; boardId: string; canEdit: boolean }) {
  const { column, cards, totalCount, children, handleProps, isOver } = p;
  const { pointerProps, keyProps } = splitHandle(handleProps);
  const look = columnLook(column.color);
  return (
    <section
      id={`col-${column.id}`}
      aria-label={`Spalte ${column.title}`}
      className={cx('flex max-h-full min-h-0 w-[min(85vw,300px)] flex-col rounded-2xl sm:w-[288px]', look.className, isOver && 'ring-2 ring-accent/40')}
      style={look.style}
    >
      <header
        {...pointerProps}
        className={cx('flex items-center gap-1.5 py-1.5 pr-1 pl-3', canEdit && 'cursor-grab touch-manipulation active:cursor-grabbing')}
      >
        {/* Tastatur-Griff nur am Titel – das Menü daneben ist kein Teil des Griffs (keine verschachtelten Bedienelemente) */}
        <h2 className="min-w-0 flex-1 text-sm font-semibold">
          <span {...keyProps} className="block truncate rounded outline-none focus-visible:ring-2 focus-visible:ring-accent">
            {column.title}
          </span>
        </h2>
        <RulesHint column={column} />
        <CountBadge column={column} total={totalCount} shown={cards.length} />
        {canEdit && <ColumnMenu boardId={boardId} column={column} cardCount={totalCount} />}
      </header>
      <ColumnAggregates boardId={boardId} column={column} cards={cards} filtered={cards.length !== totalCount} className="px-3 pb-1.5" />
      <div className="flex min-h-14 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain px-2 pb-2" data-scroll-root>
        {children}
        {cards.length === 0 && (
          <div className="flex min-h-16 items-center justify-center rounded-xl border-2 border-dashed border-[var(--line)] text-xs text-[var(--ink-soft)]">
            {totalCount > 0 ? 'Keine passenden Karten' : 'Hierher ziehen'}
          </div>
        )}
      </div>
      {canEdit && (
        <div className="px-2 pb-2">
          <AddCard boardId={boardId} columnId={column.id} />
        </div>
      )}
    </section>
  );
}

/* ---------- Listen-Gruppe ---------- */

export function ListGroup({ p, boardId, canEdit }: { p: ColumnRenderProps; boardId: string; canEdit: boolean }) {
  const { column, cards, totalCount, children, isOver } = p;
  const [collapsed, setCollapsed] = useState(false);
  const look = columnLook(column.color);
  return (
    <section
      aria-label={`Gruppe ${column.title}`}
      className={cx(
        'mb-3 overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--panel)]',
        isOver && 'ring-2 ring-accent/40',
      )}
    >
      <header className={cx('flex items-center gap-1 pr-1', look.className)} style={look.style}>
        <button
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          className="flex min-h-11 flex-1 items-center gap-1.5 px-2.5 text-left text-sm font-semibold"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          <span className="truncate">{column.title}</span>
        </button>
        <RulesHint column={column} />
        <CountBadge column={column} total={totalCount} shown={cards.length} />
        {canEdit && <ColumnMenu boardId={boardId} column={column} cardCount={totalCount} />}
      </header>
      <ColumnAggregates boardId={boardId} column={column} cards={cards} filtered={cards.length !== totalCount} className={cx('px-3 py-1', look.className)} />
      {!collapsed && (
        <>
          <div className="flex flex-col divide-y divide-[var(--line)]">{children}</div>
          {cards.length === 0 && <div className="px-3 py-3 text-xs text-[var(--ink-soft)]">{totalCount > 0 ? 'Keine passenden Karten' : 'Keine Karten – hierher ziehen'}</div>}
          {canEdit && (
            <div className="border-t border-[var(--line)] px-1.5 py-1">
              <AddCard boardId={boardId} columnId={column.id} compact />
            </div>
          )}
        </>
      )}
    </section>
  );
}

/* ---------- Karten-Aktionen ---------- */

export function CardMenu({
  card,
  onImage,
  onOpen,
  onMove,
  onArchive,
}: {
  card: CardSummary;
  onImage: boolean;
  onOpen: () => void;
  onMove: () => void;
  onArchive: () => void;
}) {
  return (
    <DropdownMenu modal={false}>
      <DropdownTrigger asChild>
        <button
          type="button"
          aria-label={`Aktionen für „${card.title}“`}
          className={menuButtonClass(onImage)}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal size={18} />
        </button>
      </DropdownTrigger>
      <DropdownContent>
        <DropdownItem icon={<Maximize2 size={16} />} onSelect={onOpen}>
          Öffnen
        </DropdownItem>
        <DropdownItem icon={<ArrowRightLeft size={16} />} onSelect={onMove}>
          Verschieben nach …
        </DropdownItem>
        <DropdownItem
          icon={<LayoutTemplate size={16} />}
          onSelect={async () => {
            const name = window.prompt('Name der Kartenvorlage', card.title);
            if (!name?.trim()) return;
            try {
              await api.post(`/api/cards/${card.id}/save-as-template`, { name: name.trim() });
              toast.info(`Vorlage „${name.trim()}“ gespeichert.`);
            } catch (err) {
              toast.error((err as Error).message);
            }
          }}
        >
          Als Vorlage speichern
        </DropdownItem>
        <DropdownItem icon={<Archive size={16} />} onSelect={onArchive}>
          Archivieren
        </DropdownItem>
      </DropdownContent>
    </DropdownMenu>
  );
}

export { CardTile };

/* ---------- Listenzeile ---------- */

export const ListRow = memo(function ListRow({
  card,
  labels,
  members,
  dragging,
  menu,
  fields,
  density = 'standard',
  waiting = 0,
  conflict = false,
}: {
  card: CardSummary;
  labels: Map<string, Label>;
  members: Map<string, Member>;
  dragging: boolean;
  menu?: (card: CardSummary, onImage: boolean) => React.ReactNode;
  fields?: CustomField[];
  density?: Density;
  waiting?: number;
  conflict?: boolean;
}) {
  const compact = density === 'compact';
  const visual = density === 'visual';
  const due = card.dueAt ? dueState(card.dueAt, card.dueHasTime, card.completedAt) : 'none';
  const assignees = card.assigneeIds.map((id) => members.get(id)).filter((m): m is Member => !!m);
  const cardLabels = card.labelIds.map((id) => labels.get(id)).filter((l): l is Label => !!l);
  return (
    <div
      className={cx(
        'kaba-card group flex items-center gap-3 bg-[var(--panel)] px-3 hover:bg-black/[0.02] dark:hover:bg-white/[0.03]',
        compact ? 'min-h-11 py-1.5' : 'min-h-14 py-2',
        dragging && 'opacity-40',
      )}
    >
      {compact ? null : card.cover?.type === 'image' && card.cover.url ? (
        <img
          src={card.cover.srcSet ? card.cover.url.replace(/\/\d+$/, '/320') : card.cover.url}
          alt=""
          draggable={false}
          loading="lazy"
          className={cx('shrink-0 rounded-md object-cover', visual ? 'h-16 w-24' : 'size-10')}
          style={{ objectPosition: `${card.cover.focusX * 100}% ${card.cover.focusY * 100}%` }}
        />
      ) : card.cover?.type === 'color' ? (
        <span className={cx('shrink-0 rounded-md', visual ? 'h-16 w-24' : 'size-10', LABEL_BG[card.cover.color ?? 'gray'])} />
      ) : null}
      <div className="min-w-0 flex-1">
        <p className={cx(compact ? 'line-clamp-1' : 'line-clamp-2', 'text-[15px] leading-snug sm:text-sm', card.completedAt && 'line-through opacity-70')}>
          {card.title}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--ink-soft)]">
          {card.blocked && (
            <span className="inline-flex items-center gap-1 rounded bg-rose-600 px-1 text-white">
              <Ban size={11} /> Blockiert
            </span>
          )}
          {waiting > 0 && (
            <span className={cx('inline-flex items-center gap-1 rounded px-1', conflict ? 'bg-amber-400 text-amber-950' : 'bg-black/5 dark:bg-white/10')} title="Wartet auf offene Karten">
              <Link2 size={11} /> {waiting}
            </span>
          )}
          {cardLabels.map((l) => (
            <span key={l.id} className={cx('inline-block h-2 w-6 rounded-full', LABEL_BG[l.color])} title={l.name || undefined} />
          ))}
          {card.dueAt && (
            <span
              className={cx(
                'inline-flex items-center gap-1 rounded px-1',
                due === 'overdue' && 'bg-rose-600 text-white',
                due === 'soon' && 'bg-amber-400 text-amber-950',
                due === 'done' && 'bg-emerald-600 text-white',
              )}
            >
              <Clock size={11} /> {formatDue(card.dueAt, card.dueHasTime)}
            </span>
          )}
          {card.checklistTotal > 0 && (
            <span>
              ☑ {card.checklistDone}/{card.checklistTotal}
            </span>
          )}
          {card.commentCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <MessageSquare size={11} /> {card.commentCount}
            </span>
          )}
        </div>
        <div className="mt-1 empty:hidden">
          {!compact && <FieldChips card={card} fields={fields} members={members} />}
        </div>
      </div>
      {assignees.length > 0 && (
        <span className="flex -space-x-1.5">
          {assignees.slice(0, 3).map((m) => (
            <Avatar key={m.id} user={m} size={24} />
          ))}
        </span>
      )}
      {menu?.(card, false)}
    </div>
  );
});

/** Ziehgriff aufteilen: Zeiger-Ereignisse für den ganzen Kopf, Rolle/Fokus/Tastatur nur für den Titel */
function splitHandle(handle: Record<string, unknown>) {
  const pointerProps: Record<string, unknown> = {};
  const keyProps: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(handle)) {
    if (k.startsWith('on') && k !== 'onKeyDown') pointerProps[k] = v;
    else keyProps[k] = v;
  }
  return { pointerProps, keyProps };
}
